import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Link } from '@tanstack/react-router';
import { FeatureSpec } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Accordion, Button, Text } from '@frontend/design-system';
import { UserRole } from '../../utils/constants';
import { checkRbacAccess, formatString } from '../../utils/helpers';
import { getIconButtonStyle } from '../../utils/styles';
import { IntegrationsMetadata, RoleBasedValue, SupportedFeature } from '../../utils/typings';

interface SupportedFeaturesTabProps {
  integrationsMetadata?: IntegrationsMetadata;
  supportedFeatures?: FeatureSpec[];
  userRole?: keyof typeof UserRole;
}

/**
 * Type guard to check whether a value follows the RoleBasedValue shape.
 * This helps distinguish RBAC-configured fields from primitive values.
 */
const isRoleBasedValue = (value: RoleBasedValue): boolean =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Utility to determine whether a string should be rendered as a clickable link.
 */
const isHttpLink = (value: string): boolean => typeof value === 'string' && value.startsWith('http');

const SupportedFeaturesTab: React.FC<SupportedFeaturesTabProps> = ({
  integrationsMetadata,
  supportedFeatures = [],
  userRole,
}) => {
  /**
   * Memoized RBAC computation to avoid recalculating access rules on every render.
   */
  const { viewableFeatureKeys, hasSupportedFeaturesAccess } = useMemo(() => {
    // RBAC configuration for supported features section
    const rbacConfig = integrationsMetadata?.supportedFeatures ?? {};

    // Overall access check for the Supported Features section
    const hasAccess = checkRbacAccess(rbacConfig, userRole).hasViewAccess;

    /**
     * Compute per-feature visibility based on RBAC rules.
     * The result is a lookup map like: { description: true, helpArticleUrl: false }
     */
    const computedViewableFeatureKeys = Object.entries(rbacConfig).reduce<Record<string, boolean>>(
      (acc, [key, config]) => {
        acc[key] = isRoleBasedValue(config) ? checkRbacAccess(config, userRole).hasViewAccess : false;
        return acc;
      },
      {}
    );

    return {
      viewableFeatureKeys: computedViewableFeatureKeys,
      hasSupportedFeaturesAccess: hasAccess,
    };
  }, [integrationsMetadata, userRole]);

  /**
   * Early return when:
   * - No supported features exist
   * - User does not have RBAC access to view them
   */
  if (supportedFeatures.length === 0 || !hasSupportedFeaturesAccess) {
    return (
      <Text textAlign='left' css={{ marginTop: theme.spacing(2) }}>
        No supported features available.
      </Text>
    );
  }

  const renderAccordionItem = (feature: FeatureSpec) => (
    <Accordion.Item key={feature.feature} value={feature.feature || ''}>
      <Accordion.Header>
        <Text weight='bold' css={textAlignStyle}>
          <Icon name='confirm-badge' color='success' />
          {feature.feature}
        </Text>
      </Accordion.Header>

      <Accordion.Body>
        <div css={bodyContainerStyle}>
          <div css={detailsStyle}>
            {Object.entries(feature)
              .filter(([key]) => key !== 'feature' && viewableFeatureKeys[key as keyof SupportedFeature])
              .map(([key, value]) => (
                <div key={key} css={featureItemStyle}>
                  <Text as='div' css={featureItemContentStyle}>
                    {formatString(key)} :{' '}
                    {isHttpLink(value) ? (
                      <Link to={value} target='_blank' rel='noopener noreferrer'>
                        {value}
                      </Link>
                    ) : (
                      <span>{value}</span>
                    )}
                  </Text>
                </div>
              ))}
          </div>
          <Button aria-label='Edit' css={getIconButtonStyle(theme.colors.black)}>
            <Icon name='edit' size={20} />
          </Button>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );

  const leftColumn = supportedFeatures.filter((_, index) => index % 2 === 0);
  const rightColumn = supportedFeatures.filter((_, index) => index % 2 !== 0);

  return (
    <div css={containerStyle}>
      <div css={headerStyle}>
        <Button aria-label='Add New Supported Feature' iconName='add' variant='tertiary' size='large'>
          Add New Supported Feature
        </Button>
      </div>
      <Accordion variant='main' isMulti css={accordionStyle} showBoxShadow>
        <div css={columnStyle}>{leftColumn.map(renderAccordionItem)}</div>
        <div css={columnStyle}>{rightColumn.map(renderAccordionItem)}</div>
      </Accordion>
    </div>
  );
};

export default SupportedFeaturesTab;

const containerStyle = css`
  display: flex;
  flex-direction: column;
  margin-top: ${theme.spacing(3)};
`;

const headerStyle = css`
  display: flex;
  justify-content: flex-start;
  margin-bottom: ${theme.spacing(2)};
`;

const accordionStyle = css`
  display: flex;
  gap: ${theme.spacing(2)};
  align-items: flex-start;
`;

const columnStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${theme.spacing(0.5)};
  min-width: 0;
`;

const featureItemStyle = css`
  display: flex;

  &::before {
    content: '•';
    margin-right: ${theme.spacing(0.5)};
  }
`;

const featureItemContentStyle = css`
  margin-bottom: ${theme.spacing(1.5)};
`;

const textAlignStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
`;

const bodyContainerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const detailsStyle = css`
  flex: 1;
`;
